import { Controller } from 'stimulus'

/**
 * Manages js for conditional next partial (StimulusJS controller)
 *
 * @class ConditionalNextController
 * @memberof Controllers
 *
 * @example
 *
 * -# Example Markup (HAML)
 *  = hidden_field_tag 'next_visible', 'maybe', data: { target: 'refinance-application.nextVisible' }
 *
 * .....
 * .....
 * .....
 *
 *  .checkbox{ 'data-controller': 'conditional-next' } }
 *    = label_tag :consented do
 *      = check_box_tag :consented ... data: { target: 'conditional-next.field', action: 'conditional-next#maybeToggleNext' }
 *
 *  In the case that a field has a default value other than '', you can add an
 *  optional 'default-value' to the dataset and the toggle will not go enabled for both '' and that default value:
 *
 *  .field{ 'data-controller': 'conditional-next' } }
 *    = label_tag :dependents do
 *      = phone_field_tag :dependents, 0 ... data: {
 *        target: 'conditional-next.field',
 *        action: 'input->conditional-next#maybeToggleNext',
 *        'default-value': '0',
 *        behavior: 'input-masking-number'
 *      }
 *
 */
export default class extends Controller {
  static targets = [
    'field'
  ]

  /**
   * @function Controllers.ConditionalNextController.connect
   * @memberof Controllers
   * @description Triggers maybeToggleNext on connect to start toggle state.
   * Sets next state even when traversing back to previous steps.
   *
   * @return {undefined} manipulates the DOM.
   *
   */
  connect() {
    this.maybeToggleNext()
  }

  /**
   * @function Controllers.ConditionalNextController.maybeToggleNext
   * @memberof Controllers
   * @description Calls out to to toggle the state of the next button.
   *
   * @return {undefined} manipulates the DOM.
   *
   */
  maybeToggleNext() {
    const allGood = this.fieldTargets.every(field => {
      if (field.type === 'checkbox') {
        return field.checked
      } else if (field.type === 'radio') {
        const nameAttr = field.name
        return document.querySelectorAll(`input[name=${nameAttr}]:checked`).length > 0
      } else {
        return this._fieldHasAcceptableValue(field)
      }
    })

    if (allGood) {
      window.dispatchEvent(new CustomEvent('next-on'))
    } else {
      window.dispatchEvent(new CustomEvent('next-off'))
    }
  }

  /**
   * @function Controllers.ConditionalNextController._fieldHasAcceptableValue
   * @memberof Controllers
   * @description Calls out to to toggle the state of the next button.
   *
   * @param {Element} field - DOM element of an input field
   *
   * @return {Boolean} boolean representing whether the fields value is
   * acceptable or not
   *
   */
  _fieldHasAcceptableValue(field) {
    if (field.dataset.defaultValue) {
      return field.value !== '' && field.value !== field.dataset.defaultValue
    } else {
      return field.value !== ''
    }
  }
}
